import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate, useLocation } from "react-router-dom";

const formSchema = z.object({
	email: z.string().email({
		message: "有効なメールアドレスを入力してください。",
	}),
	password: z.string().min(8, {
		message: "パスワードは8文字以上である必要があります。",
	}),
});

interface LocationState {
	from: { pathname: string };
}

const LoginForm: React.FC = () => {
	const { login, loading, error } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();
	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			email: "",
			password: "",
		},
	});

	const onSubmit = async (values: z.infer<typeof formSchema>) => {
		try {
			await login(values.email, values.password);
			const state = location.state as LocationState | null;
			const from = state?.from?.pathname || "/webapp/profile";
			navigate(from, { replace: true });
		} catch (error) {
			console.error("ログインエラー:", error);
		}
	};

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
				<FormField
					control={form.control}
					name="email"
					render={({ field }) => (
						<FormItem>
							<FormLabel>メールアドレス</FormLabel>
							<FormControl>
								<Input type="email" {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={form.control}
					name="password"
					render={({ field }) => (
						<FormItem>
							<FormLabel>パスワード</FormLabel>
							<FormControl>
								<Input type="password" {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				{error && <p className="text-red-500">{error}</p>}
				<Button type="submit" className="w-full" disabled={loading}>
					{loading ? "ログイン中..." : "ログイン"}
				</Button>
			</form>
		</Form>
	);
};

export default LoginForm;
